import { Header } from "./-components/header"
import { useEffect } from "react"
import { createFileRoute } from "@tanstack/react-router"

export const Route = createFileRoute("/(site)/refunds")({
  component: RefundPolicy,
})

function RefundPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-24 max-w-4xl">
        <article className="prose prose-slate dark:prose-invert max-w-none">
          <h1>Refund Policy for Knowledge Facts Ladder (KFL)</h1>
          <p className="text-muted-foreground">Last updated: 03.12.2025</p>

          <p>
            This Refund Policy explains when players are eligible for refunds
            for payments made to Knowledge Facts Ladder ("KFL", "we", "our"). By
            subscribing to or participating in KFL, you agree to the terms
            described here.
          </p>

          <h2>1. General Policy</h2>
          <p>
            1.1. All payments to KFL are final and non-refundable, except in the
            specific circumstances described below.
          </p>
          <p>
            1.2. Players are responsible for reviewing subscription terms and
            event access before purchasing.
          </p>
          <p>
            1.3. Participation in any quiz, event, or activity constitutes
            acceptance of the payment conditions.
          </p>

          <h2>2. Refund Eligibility</h2>

          <h3>2.1. Double Charges</h3>
          <p>
            You are eligible for a refund only if you were charged twice for the
            same subscription or payment. A "double charge" means:
          </p>
          <ul>
            <li>
              two identical payments processed for the same subscription period,
            </li>
            <li>two identical payments for the same credit purchase,</li>
            <li>
              or two identical payments within a short timeframe that were
              clearly not intended.
            </li>
          </ul>
          <p>If this occurs, KFL will refund the duplicated amount in full.</p>

          <h3>2.2. Non-Eligible Situations</h3>
          <p>Refunds will not be issued for:</p>
          <ul>
            <li>changing your mind after purchase</li>
            <li>unused subscription time</li>
            <li>unused credits</li>
            <li>dissatisfaction with quiz results or performance</li>
            <li>missed events or accidental non-participation</li>
            <li>
              technical issues on the user's device or internet connection
            </li>
            <li>late arrival to games</li>
            <li>discovered or alleged cheating</li>
            <li>account removal due to rule violations or misconduct</li>
            <li>subscription cancellations after renewal</li>
            <li>
              currency fluctuations or fees applied by your bank or payment
              provider
            </li>
          </ul>

          <h2>3. Payment Methods</h2>
          <p>KFL accepts payments through:</p>
          <ul>
            <li>
              Monobank online banking (preferred method; KFL sends you a payment
              link)
            </li>
            <li>PayPal</li>
            <li>Wise</li>
            <li>Payoneer</li>
          </ul>
          <p>
            All refunds for eligible cases (double charges) will be issued
            through the same payment method used for the original purchase
            whenever possible.
          </p>
          <p>
            KFL does not process refunds through alternative methods or cash.
          </p>

          <h2>4. Refund Request Procedure</h2>
          <p>To request a refund for a double charge:</p>
          <p>
            4.1. Contact KFL support at{" "}
            <a href="mailto:knowledge.facts.ladder@gmail.com">
              knowledge.facts.ladder@gmail.com
            </a>
            .
          </p>
          <p>4.2. Provide the following information:</p>
          <ul>
            <li>your full name</li>
            <li>account email</li>
            <li>payment date(s)</li>
            <li>payment amount(s)</li>
            <li>screenshot or receipt of the duplicate charge</li>
          </ul>
          <p>
            4.3. KFL will review the request and respond within 14 business
            days.
          </p>
          <p>KFL may request additional documentation if necessary.</p>

          <h2>5. Disputes and Chargebacks</h2>
          <p>
            5.1. If you initiate a dispute or chargeback through a payment
            provider without first contacting KFL, your account may be suspended
            during the investigation.
          </p>
          <p>
            5.2. Excessive or fraudulent chargebacks may result in a permanent
            ban.
          </p>
          <p>
            5.3. If the chargeback is resolved in KFL's favor, your account will
            be reactivated only after the disputed amount is repaid.
          </p>

          <h2>6. Subscription Cancellations</h2>
          <p>6.1. You may cancel your subscription at any time.</p>
          <p>
            6.2. Cancellation prevents future charges but does not provide a
            refund for current or past subscription periods.
          </p>
          <p>
            6.3. Access to subscription features continues until the end of the
            paid period.
          </p>

          <h2>7. Changes to This Refund Policy</h2>
          <p>
            We may update this Refund Policy at any time. The updated version
            will be posted on the website with a new "Last updated" date.
            Continued use of KFL services after changes constitutes acceptance
            of the updated Policy.
          </p>

          <h2>8. Contact</h2>
          <p>For refund questions or concerns, email:</p>
          <p>
            <a href="mailto:knowledge.facts.ladder@gmail.com">
              knowledge.facts.ladder@gmail.com
            </a>
            <br />
            <a href="https://www.kflquiz.com">www.kflquiz.com</a>
          </p>
        </article>
      </main>
    </div>
  )
}
